/**
 * SA 管理 > 設定タブ (2026-07-28)。
 *
 * SA 顧客保有期間の設定。settings/sales に保存する。
 *   - saCustomerRetentionMonths: SA が獲得した顧客をその SA の顧客とみなす期間 (ヶ月)
 *   - saRetentionReminderDays:   期限の何日前に SA へリマインダーを送るか
 *
 * 起算は「SA に紐付いた時点」と「その SA の商談で入金があった時点」。
 * 期限を過ぎた顧客は日次バッチ (saRetentionSweep) が SA から外し、弊社顧客にする。
 */
import {
  SA_RETENTION_DEFAULT_MONTHS,
  SA_RETENTION_DEFAULT_REMINDER_DAYS,
  computeRetentionUntil,
} from '@goenhub/shared';
import { CalendarClock, Info } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useSalesSettingsWithDefaults,
  useUpdateSalesSettings,
} from '@/hooks/useSettings';

export function SaRetentionSettingsTab() {
  const { merged, isLoading } = useSalesSettingsWithDefaults();
  const update = useUpdateSalesSettings();

  const [months, setMonths] = useState<string>('');
  const [reminderDays, setReminderDays] = useState<string>('');

  useEffect(() => {
    setMonths(
      String(merged.saCustomerRetentionMonths ?? SA_RETENTION_DEFAULT_MONTHS),
    );
    setReminderDays(
      String(merged.saRetentionReminderDays ?? SA_RETENTION_DEFAULT_REMINDER_DAYS),
    );
  }, [merged.saCustomerRetentionMonths, merged.saRetentionReminderDays]);

  const monthsNum = Math.max(0, Math.floor(Number(months) || 0));
  const daysNum = Math.max(0, Math.floor(Number(reminderDays) || 0));

  // 「今日 SA に紐付いたらいつまで」のプレビュー。月末クランプの挙動を目で確認できるようにする。
  const previewUntil = useMemo(() => {
    const d = computeRetentionUntil(new Date(), monthsNum);
    return d
      ? d.toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      : null;
  }, [monthsNum]);

  const handleSave = async () => {
    if (daysNum > 0 && monthsNum > 0 && daysNum > monthsNum * 28) {
      toast.error(
        'リマインダー日数が保有期間より長くなっています。期間を短くするか日数を減らしてください。',
      );
      return;
    }
    try {
      await update.mutateAsync({
        saCustomerRetentionMonths: monthsNum,
        saRetentionReminderDays: daysNum,
      });
      toast.success('保存しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-lg space-y-5 pt-4">
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="size-4 text-[var(--accent)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            顧客の保有期間
          </h3>
        </div>

        <div>
          <Label className="text-xs">保有期間 (ヶ月)</Label>
          <Input
            type="number"
            min={0}
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            className="max-w-[140px]"
          />
          <p className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
            SA が獲得した顧客を「その SA の顧客」とみなす期間。
            起算は <strong>SA に紐付いた時点</strong> と{' '}
            <strong>その SA の商談で入金があった時点</strong>。
            期間内に入金がなければ SA から外れ、弊社の顧客になります。
            <br />
            <strong>0 を指定すると無期限</strong>（この機能が実質 OFF になり、
            期限切れによる解除も行われません）。
          </p>
          {monthsNum > 0 && previewUntil && (
            <p className="text-[11px] text-[var(--accent)] mt-1">
              例: 今日 SA に紐付いた顧客の期限は <strong>{previewUntil}</strong>{' '}
              まで（月末は繰り上がらずクランプ: 1/31 + 1ヶ月 = 2/28）
            </p>
          )}
        </div>

        <div>
          <Label className="text-xs">期限切れリマインダー (何日前)</Label>
          <Input
            type="number"
            min={0}
            value={reminderDays}
            onChange={(e) => setReminderDays(e.target.value)}
            className="max-w-[140px]"
          />
          <p className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
            保有期限がこの日数以内に迫った顧客について、担当 SA へ
            「まもなく顧客期限が切れます」とメールで通知します
            （SA ごとに 1 通へまとめて送信）。
            <br />
            <strong>0 を指定すると通知しません。</strong>
          </p>
        </div>

        <div className="flex justify-end pt-1">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={update.isPending}
          >
            {update.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 flex gap-2">
        <Info className="size-3.5 text-[var(--text-muted)] flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-[var(--text-muted)] space-y-1 leading-relaxed">
          <p>
            <strong>リマインダーメールの文面</strong>は
            「メール ＞ 業務通知テンプレート」の{' '}
            <strong>【SA】担当顧客の保有期限が近づいています</strong>{' '}
            で作成・有効化してください。テンプレが無い間はメールは送信されません
            （期限切れによる解除は文面が無くても実行されます）。
          </p>
          <p>
            設定を変更しても<strong>既存顧客の期限は再計算されません</strong>。
            次に入金でリセットされたとき、または顧客詳細から手動で延長したときに
            新しい期間が適用されます。
          </p>
        </div>
      </div>
    </div>
  );
}
